import { Alerta, Button, DataList, Dialog, MetricCard, MontoField, Row, Select, Spinner, Stack, TextField } from "@/components";
import type { FilaGanancia, TotalesGanancia } from "@/lib/api";
import { formatFecha, formatMoneda } from "@/lib/format";
import { formatPorcentajeA, formatRentabilidad } from "@/lib/ganancia";
import { useGananciaTab } from "./useGananciaTab";

function Tarjetas({ datos, repartida }: { datos: TotalesGanancia; repartida: boolean }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <MetricCard titulo="Aportado" valor={formatMoneda(datos.aportado_usd, "USD")} detalle="Para la parte clase A de las previsiones" />
      <MetricCard titulo={repartida ? "Cobrado" : "Le tocaría hoy"} valor={formatMoneda(datos.cobrado_usd, "USD")} detalle={repartida ? "Del reparto de la ganancia" : "Si se repartiera hoy lo disponible"} />
      <MetricCard titulo="Ganancia" valor={formatMoneda(datos.ganancia_usd, "USD")} detalle={`Rentabilidad: ${formatRentabilidad(datos.rentabilidad_pct)}`} />
    </div>
  );
}

export function GananciaTab() {
  const t = useGananciaTab();
  const g = t.ganancia;
  const sinPlata = !g || Number(g.disponible_ventas_usd) <= 0 || g.filas.length === 0;

  const columnas = [
    { clave: "nombre", titulo: "Fiduciante", render: (f: FilaGanancia) => f.nombre },
    { clave: "pct", titulo: "% en clase A", render: (f: FilaGanancia) => formatPorcentajeA(f.porcentaje_a) },
    { clave: "aportado", titulo: "Aportado", render: (f: FilaGanancia) => formatMoneda(f.aportado_usd, "USD") },
    { clave: "cobrado", titulo: t.repartida ? "Cobrado" : "Le tocaría hoy", render: (f: FilaGanancia) => formatMoneda(f.cobrado_usd, "USD") },
    { clave: "ganancia", titulo: "Ganancia", render: (f: FilaGanancia) => formatMoneda(f.ganancia_usd, "USD") },
    { clave: "rentabilidad", titulo: "Rentabilidad", render: (f: FilaGanancia) => formatRentabilidad(f.rentabilidad_pct) },
  ];

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Ganancia</h1>
        {t.esAdmin && g && !t.repartida && (
          <Button onClick={t.abrirRepartir} disabled={sinPlata}>
            Repartir la ganancia
          </Button>
        )}
      </Row>
      <p className="text-chico text-texto-suave">
        La plata de las ventas es sólo de los fiduciantes clase A (los que no eligieron depto). Se reparte una sola vez, al final, por el % de cada uno dentro de los A.
      </p>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}

      {g && (
        <>
          {g.reparto ? (
            <Row justificar="entre" envolver espacio={3} alinear="centro">
              <p>
                Repartida el {formatFecha(g.reparto.fecha)}: <strong>{formatMoneda(g.reparto.monto_usd, "USD")}</strong>
                {g.reparto.nota && ` · ${g.reparto.nota}`}
              </p>
              {t.esAdmin && (
                <Button variante="fantasma" tamanio="sm" onClick={t.abrirBorrar}>
                  Deshacer el reparto
                </Button>
              )}
            </Row>
          ) : (
            <p>
              Todavía no se repartió. Disponible de ventas hoy: <strong>{formatMoneda(g.disponible_ventas_usd, "USD")}</strong>.
            </p>
          )}

          <Tarjetas datos={g.totales} repartida={t.repartida} />
          <DataList etiqueta="Ganancia por fiduciante" columnas={columnas} filas={g.filas} claveFila={(f) => f.fiduciante_id} vacio="No hay fiduciantes clase A en la distribución vigente." />
        </>
      )}

      <Dialog abierto={t.dialogo === "repartir"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Repartir la ganancia">
        <form onSubmit={(e) => void t.repartir(e)} noValidate>
          <Stack espacio={4}>
            <p className="text-chico text-texto-suave">Se reparte una sola vez, entre los clase A de la distribución vigente a esa fecha. Si te equivocás, lo podés deshacer.</p>
            <TextField etiqueta="Fecha" type="date" value={t.form.fecha} onChange={(e) => t.cambiar("fecha", e.target.value)} error={t.errores.fecha} />
            <MontoField etiqueta="Monto a repartir" valor={t.form.monto_usd} onCambio={(v) => t.cambiar("monto_usd", v)} moneda="USD" error={t.errores.monto_usd} ayuda={g ? `Disponible de ventas: ${formatMoneda(g.disponible_ventas_usd, "USD")}` : undefined} />
            {t.opcionesCaja && <Select etiqueta="Caja" opciones={t.opcionesCaja} valor={t.form.caja_id} onCambio={(v) => t.cambiar("caja_id", v)} placeholder="Elegí la caja" />}
            <TextField etiqueta="Nota" ayuda="Opcional" value={t.form.nota} onChange={(e) => t.cambiar("nota", e.target.value)} error={t.errores.nota} />
            <Alerta texto={t.errorRepartir} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.repartiendo}>
                Repartir
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={t.dialogo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Deshacer el reparto">
        <Stack espacio={4}>
          <p>¿Deshacer el reparto de la ganancia? La plata vuelve a quedar disponible de ventas y salen de la caja las líneas del reparto.</p>
          <Alerta texto={t.errorBorrar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
              Deshacer
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </Stack>
  );
}
